import { useCallback, useEffect, useRef, useState } from 'react';
import { hasSeenIntro, markIntroSeen } from '../utils/introSeen';
import { hasLgpdConsent } from '../utils/lgpdConsent';
import { trackEvent } from '../utils/tracking';

// Apresentação do ícone de CONVERSAS do WhatsApp (layout.vm @v10, header.whatsapp.inbox.intro).
// Medido/relatado (27/10/2026): os donos não clicam no ícone porque não percebem que tem algo ali.
// Regra (André): todo dono com WhatsApp LIGADO vê uma vez, por navegador; "Entendi" e não vê mais.
export const WHATSAPP_INBOX_INTRO_ID = 'whatsapp_inbox_v1';

// Espera a casca assentar (saldo, settings, menu) antes de apontar para o ícone — sem isso o balão
// aparece e o ícone ainda nem está no lugar. Depois, a cada volta confere se o LGPD já foi aceito:
// o modal de consentimento abre na 1ª visita e o balão não pode abrir por cima dele.
const PRIMEIRA_ESPERA_MS = 1500;
const REVISAO_MS = 2000;

/**
 * Decide se o balão abre AGORA. Pura, para teste.
 * @param {{inboxVisible:boolean, seen:boolean, lgpdConsented:boolean, inboxOpen:boolean}} s
 */
export function shouldShowInboxIntro({ inboxVisible, seen, lgpdConsented, inboxOpen }) {
  return Boolean(inboxVisible && !seen && lgpdConsented && !inboxOpen);
}

/**
 * @param {{ inboxVisible:boolean, inboxOpen:boolean, userId?:string|null }} p
 * @returns {{ open:boolean, dismiss:(reason:'confirm'|'opened'|'close')=>void }}
 */
export default function useWhatsAppInboxIntro({ inboxVisible, inboxOpen, userId }) {
  const [seen, setSeen] = useState(() => hasSeenIntro(WHATSAPP_INBOX_INTRO_ID, userId));
  const [open, setOpen] = useState(false);
  const shownTracked = useRef(false);

  // Troca de conta sem recarregar a página: relê o "visto" da conta nova.
  useEffect(() => { setSeen(hasSeenIntro(WHATSAPP_INBOX_INTRO_ID, userId)); }, [userId]);

  useEffect(() => {
    if (!inboxVisible || seen || inboxOpen) { setOpen(false); return undefined; }
    let timer;
    const tentar = (espera) => {
      timer = setTimeout(() => {
        if (shouldShowInboxIntro({ inboxVisible, seen, lgpdConsented: hasLgpdConsent(), inboxOpen })) {
          setOpen(true);
          if (!shownTracked.current) {
            shownTracked.current = true;
            trackEvent('whatsapp_inbox_intro_shown');
          }
        } else {
          tentar(REVISAO_MS);
        }
      }, espera);
    };
    tentar(PRIMEIRA_ESPERA_MS);
    return () => clearTimeout(timer);
  }, [inboxVisible, seen, inboxOpen]);

  const dismiss = useCallback((reason) => {
    if (seen) return;
    markIntroSeen(WHATSAPP_INBOX_INTRO_ID, userId);
    setSeen(true);
    setOpen(false);
    trackEvent('whatsapp_inbox_intro_dismiss', { reason });
  }, [seen, userId]);

  return { open, dismiss };
}
